import { toHex } from '#library/number.library.js';
import { objectify } from '#library/serialize.library.js';
import { asString, asError } from '#library/coercion.library.js';
import { isError, isString, isPlainObject, isText, RE_WHITESPACES } from '#library/assertion.library.js';
import { bufferToBase64, base64ToBuffer, encodeBuffer, encodeText, decodeBuffer } from '#library/buffer.library.js';
const crypto = globalThis.crypto;
const subtle = crypto.subtle;
/**
 * Cryptographic algorithm and encoding constants used throughout the cipher library.
 *
 * @property Algorithm - Default hash algorithm for digest operations
 * @property Encoding - Default text encoding for string conversion
 * @property SignKey - Asymmetric signature algorithm identifier
 * @property TypeKey - Symmetric encryption algorithm identifier
 */
export const keys = {
    Algorithm: 'SHA-256',
    Encoding: 'utf-8',
    SignKey: 'RSASSA-PKCS1-v1_5',
    TypeKey: 'AES-GCM',
};
// Module-scoped state for ephemeral keys (lazily instantiated)
let _cryptoKeyPromise;
const getCryptoKey = () => (_cryptoKeyPromise ??= subtle
    .generateKey({ name: keys.TypeKey, length: 128 }, false, ['encrypt', 'decrypt'])
    .catch(asError));
let _asymmetricKeyPromise;
const getAsymmetricKey = () => (_asymmetricKeyPromise ??= subtle.generateKey({
    name: keys.SignKey,
    modulusLength: 2048,
    publicExponent: new Uint8Array([1, 0, 1]),
    hash: { name: keys.Algorithm },
}, false, ['sign', 'verify'])
    .catch(asError));
/**
 * Generates a random, short UUID key based on standard Web Crypto API UUIDs.
 *
 * @returns A randomly generated short string
 * @example
 * ```ts
 * const key = randomKey(); // 'e8b7a421'
 * ```
 */
export const randomKey = () => crypto.randomUUID().split('-')[0];
/**
 * Generates a Hash-based Message Authentication Code (HMAC) for a given source payload.
 *
 * @param source - The data to hash
 * @param secret - The secret key used for hashing
 * @param alg - The hash algorithm to use (default: 'SHA-512')
 * @param len - Optional length to truncate the resulting hex string
 * @returns A promise resolving to the HMAC hex string
 */
export const hmac = async (source, secret, alg = 'SHA-512', len) => {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(asString(source));
    const key = await subtle.importKey('raw', keyData, { name: 'HMAC', hash: { name: alg } }, false, ['sign']);
    const signature = await subtle.sign('HMAC', key, messageData);
    return toHex(Array.from(new Uint8Array(signature)), len);
};
/**
 * Computes a cryptographic hash digest for the provided source payload.
 *
 * @param source - The data to hash
 * @param len - Optional length to truncate the resulting hex string
 * @param alg - The hash algorithm to use (default: 'SHA-256')
 * @returns A promise resolving to the hash hex string
 */
export const hash = async (source, len, alg = 'SHA-256') => {
    const buffer = encodeText(asString(source));
    const hashBuf = await subtle.digest(alg, buffer);
    return toHex(Array.from(new Uint8Array(hashBuf)), len);
};
/**
 * Encrypts arbitrary data using AES-GCM and a module-scoped ephemeral symmetric key.
 *
 * @param data - The data to encrypt
 * @returns A promise resolving to the Base64-encoded encrypted string (including IV)
 */
export const encrypt = async (data) => {
    const iv = crypto.getRandomValues(new Uint8Array(16));
    const key = await getCryptoKey();
    if (isError(key))
        throw new Error(`Cipher: Key generation failed: ${key.message}`, { cause: key });
    const cipherBuf = await subtle.encrypt({ name: keys.TypeKey, iv }, key, encodeBuffer(data));
    const combined = new Uint8Array(16 + cipherBuf.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuf), 16);
    return bufferToBase64(combined);
};
/**
 * Decrypts a Base64-encoded encrypted string using the module-scoped ephemeral symmetric key.
 *
 * @param secret - The encrypted string (or a promise resolving to one)
 * @returns A promise resolving to the decrypted, deserialized data
 */
export const decrypt = async (secret) => {
    const [str, key] = await Promise.all([secret, getCryptoKey()]);
    if (isError(key))
        throw new Error(`Cipher: Key generation failed: ${key.message}`, { cause: key });
    const uint8 = base64ToBuffer(str);
    const iv = uint8.slice(0, 16);
    const data = uint8.slice(16);
    return subtle.decrypt({ name: keys.TypeKey, iv }, key, data)
        .then(result => new Uint8Array(result))
        .then(decodeBuffer)
        .then(objectify);
};
/**
 * Signs arbitrary data using RSASSA-PKCS1-v1_5 and a module-scoped ephemeral asymmetric keypair.
 *
 * @param doc - The data to sign
 * @returns A promise resolving to the Uint8Array signature
 */
export const sign = async (doc) => {
    const keypair = await getAsymmetricKey();
    if (isError(keypair))
        throw new Error(`Cipher: Key generation failed: ${keypair.message}`, { cause: keypair });
    if (!keypair.privateKey)
        throw new Error('Cipher: Missing private key');
    return subtle.sign(keys.SignKey, keypair.privateKey, encodeBuffer(doc))
        .then(result => new Uint8Array(result));
};
/**
 * Verifies a signature against the provided data using the module-scoped ephemeral asymmetric public key.
 *
 * @param signature - The signature buffer to verify
 * @param doc - The original data payload
 * @returns A promise resolving to a boolean indicating verification success
 */
export const verify = async (signature, doc) => {
    const [buffer, keypair] = await Promise.all([signature, getAsymmetricKey()]);
    if (isError(keypair))
        throw new Error(`Cipher: Key generation failed: ${keypair.message}`, { cause: keypair });
    if (!keypair.publicKey)
        throw new Error('Cipher: Missing public key');
    return subtle.verify(keys.SignKey, keypair.publicKey, buffer, encodeBuffer(doc));
};
/**
 * Compares two byte buffers in constant time to prevent timing attacks.
 *
 * @param a - First byte buffer
 * @param b - Second byte buffer
 * @returns True if both buffers are identical in length and content
 * @example
 * ```ts
 * const match = timingSafeEqual(sigA, sigB);
 * ```
 */
export const timingSafeEqual = (a, b) => {
    if (a.length !== b.length)
        return false;
    let mismatch = 0;
    for (let i = 0; i < a.length; i++) {
        mismatch |= a[i] ^ b[i];
    }
    return mismatch === 0;
};
/**
 * Imports a PEM-formatted public key string into a native Web Crypto API CryptoKey object.
 *
 * @param pem - The PEM-formatted public key string
 * @param alg - The signature algorithm identifier (default: keys.SignKey)
 * @param hash - The hash algorithm identifier (default: keys.Algorithm)
 * @returns A promise resolving to the imported CryptoKey
 */
export const importPublicKey = async (pem, alg = keys.SignKey, hash = keys.Algorithm) => {
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    if (!pem || !pem.includes(pemHeader) || !pem.includes(pemFooter))
        throw new Error('Invalid PEM key format');
    const pemContents = pem
        .substring(pem.indexOf(pemHeader) + pemHeader.length, pem.indexOf(pemFooter))
        .replace(RE_WHITESPACES, '');
    const binaryDer = base64ToBuffer(pemContents);
    return subtle.importKey('spki', binaryDer, { name: alg, hash }, false, ['verify']);
};
/**
 * Imports a PKCS#8 PEM-formatted private key string into a native Web Crypto API CryptoKey object.
 *
 * @param pem - The PEM-formatted private key string
 * @param alg - The signature algorithm identifier (default: keys.SignKey)
 * @param hash - The hash algorithm identifier (default: keys.Algorithm)
 * @returns A promise resolving to the imported CryptoKey
 */
export const importPrivateKey = async (pem, alg = keys.SignKey, hash = keys.Algorithm) => {
    const pemHeader = '-----BEGIN PRIVATE KEY-----';
    const pemFooter = '-----END PRIVATE KEY-----';
    if (!pem || !pem.includes(pemHeader) || !pem.includes(pemFooter))
        throw new Error('Invalid PEM key format');
    const pemContents = pem
        .substring(pem.indexOf(pemHeader) + pemHeader.length, pem.indexOf(pemFooter))
        .replace(RE_WHITESPACES, '');
    const binaryDer = base64ToBuffer(pemContents);
    return subtle.importKey('pkcs8', binaryDer, { name: alg, hash }, false, ['sign']);
};
/**
 * Imports a JSON Web Key (JWK) into a native Web Crypto API CryptoKey object.
 * Supports RSA keys (RS256, RS384, RS512) and symmetric oct keys (HS256, HS384, HS512).
 *
 * @param jwk - The standard JsonWebKey object
 * @param usage - Optional explicit key usages
 * @param extractable - Optional boolean indicating whether the key is extractable (default: false)
 * @returns A promise resolving to the imported CryptoKey
 */
export const importJWK = async (jwk, usage, extractable = false) => {
    if (!isPlainObject(jwk))
        throw new TypeError('Cipher: JWK must be an object');
    if (jwk.kty === 'RSA') {
        const hashName = jwk.alg === 'RS512' ? 'SHA-512' : jwk.alg === 'RS384' ? 'SHA-384' : 'SHA-256';
        const defaultUsage = jwk.d ? ['sign'] : ['verify'];
        return subtle.importKey('jwk', jwk, { name: keys.SignKey, hash: hashName }, extractable, usage ?? defaultUsage);
    }
    if (jwk.kty === 'oct') {
        const hashName = jwk.alg === 'HS512' ? 'SHA-512' : jwk.alg === 'HS384' ? 'SHA-384' : 'SHA-256';
        const defaultUsage = ['sign', 'verify'];
        return subtle.importKey('jwk', jwk, { name: 'HMAC', hash: hashName }, extractable, usage ?? defaultUsage);
    }
    throw new Error(`Cipher: Unsupported JWK key type "${jwk.kty}"`);
};
/**
 * Exports a native Web Crypto API CryptoKey object to a JSON Web Key (JWK).
 *
 * @param key - The CryptoKey to export
 * @returns A promise resolving to the exported JsonWebKey
 */
export const exportJWK = async (key) => {
    return subtle.exportKey('jwk', key);
};
/**
 * Cryptographically signs raw data using the provided CryptoKey.
 *
 * @param data - The string or binary data to sign
 * @param privateKey - The private CryptoKey with 'sign' usage
 * @param alg - The signature algorithm identifier (default: keys.SignKey)
 * @returns A promise resolving to the signature Uint8Array
 */
export const signData = async (data, privateKey, alg = keys.SignKey) => {
    const bytes = isString(data) ? encodeText(data) : data;
    const signature = await subtle.sign(alg, privateKey, bytes);
    return new Uint8Array(signature);
};
/**
 * Verifies a signature against raw data using the provided CryptoKey.
 *
 * @param signature - The signature buffer
 * @param data - The original string or binary data
 * @param publicKey - The public CryptoKey with 'verify' usage
 * @param alg - The signature algorithm identifier (default: keys.SignKey)
 * @returns A promise resolving to true if valid
 */
export const verifyData = async (signature, data, publicKey, alg = keys.SignKey) => {
    const bytes = isString(data) ? encodeText(data) : data;
    return subtle.verify(alg, publicKey, signature, bytes);
};
/**
 * Computes an HMAC signature as a raw binary buffer for the provided data and secret.
 *
 * @param data - The string or binary data to sign
 * @param secret - The secret key as a string or binary buffer
 * @param alg - The hash algorithm to use (default: 'SHA-256')
 * @returns A promise resolving to the HMAC signature Uint8Array
 */
export const signHmac = async (data, secret, alg = 'SHA-256') => {
    const secretBytes = isString(secret) ? encodeText(secret) : secret;
    const messageBytes = isString(data) ? encodeText(data) : data;
    const key = await subtle.importKey('raw', secretBytes, { name: 'HMAC', hash: { name: alg } }, false, ['sign']);
    const signature = await subtle.sign('HMAC', key, messageBytes);
    return new Uint8Array(signature);
};
/**
 * Verifies an HMAC signature against the provided data and secret using constant-time comparison.
 *
 * @param signature - The signature buffer to verify
 * @param data - The original data
 * @param secret - The secret key as a string or binary buffer
 * @param alg - The hash algorithm to use (default: 'SHA-256')
 * @returns A promise resolving to true if the HMAC signature matches
 */
export const verifyHmac = async (signature, data, secret, alg = 'SHA-256') => {
    const expected = await signHmac(data, secret, alg);
    const actual = signature instanceof Uint8Array
        ? signature
        : signature instanceof ArrayBuffer
            ? new Uint8Array(signature)
            : new Uint8Array(signature.buffer, signature.byteOffset, signature.byteLength);
    return timingSafeEqual(expected, actual);
};
/**
 * Generates a new RSASSA-PKCS1-v1_5 asymmetric keypair using the Web Crypto API.
 *
 * @returns A promise resolving to the generated CryptoKeyPair
 */
export const generateKeyPair = async () => {
    return subtle.generateKey({
        name: keys.SignKey,
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: { name: keys.Algorithm },
    }, true, ['sign', 'verify']);
};
const MFS_MAGIC = new Uint8Array([0x4D, 0x46, 0x53, 0x31]); // 'MFS1' (Magma File Store v1)
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const HEADER_LENGTH = MFS_MAGIC.length + SALT_LENGTH + IV_LENGTH; // 4 + 16 + 12 = 32 bytes
/**
 * Derives an AES-GCM 256-bit CryptoKey from a password and salt using PBKDF2 with SHA-256.
 */
async function derivePasswordKey(password, salt, iterations = 100_000, usage = ['encrypt', 'decrypt']) {
    const passBytes = isString(password) ? encodeText(password) : password;
    const baseKey = await subtle.importKey('raw', passBytes, 'PBKDF2', false, ['deriveKey']);
    return subtle.deriveKey({
        name: 'PBKDF2',
        salt: salt,
        iterations,
        hash: 'SHA-256'
    }, baseKey, { name: keys.TypeKey, length: 256 }, false, usage);
}
/**
 * Encrypts arbitrary binary or string data using a password with PBKDF2 key derivation and AES-GCM (256-bit).
 * Encapsulates: [ Magic 4B ("MFS1") | Salt 16B | IV 12B | Ciphertext + Tag ].
 *
 * @param data - Raw string or binary data to encrypt
 * @param password - Encryption password / passphrase
 * @param iterations - PBKDF2 iteration count (default: 100,000)
 * @returns Encrypted binary buffer as Uint8Array
 */
export async function encryptWithPassword(data, password, iterations = 100_000) {
    if (!isText(password))
        throw new TypeError('Cipher: A non-empty password string is required for encryption.');
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const key = await derivePasswordKey(password, salt, iterations, ['encrypt']);
    const plaintext = isString(data)
        ? encodeText(data)
        : data instanceof Uint8Array
            ? data
            : data instanceof ArrayBuffer
                ? new Uint8Array(data)
                : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    const cipherBuf = await subtle.encrypt({ name: keys.TypeKey, iv: iv }, key, plaintext);
    const result = new Uint8Array(HEADER_LENGTH + cipherBuf.byteLength);
    result.set(MFS_MAGIC, 0);
    result.set(salt, 4);
    result.set(iv, 20);
    result.set(new Uint8Array(cipherBuf), 32);
    return result;
}
/**
 * Decrypts data previously encrypted with encryptWithPassword using the supplied password.
 * Authenticates data integrity and throws an error if the password is incorrect or data was tampered with.
 *
 * @param encrypted - Encrypted binary data buffer
 * @param password - Decryption password / passphrase
 * @param iterations - PBKDF2 iteration count (default: 100,000)
 * @returns Decrypted plaintext as Uint8Array
 */
export async function decryptWithPassword(encrypted, password, iterations = 100_000) {
    if (!isText(password))
        throw new TypeError('Cipher: A non-empty password string is required for decryption.');
    const bytes = encrypted instanceof Uint8Array
        ? encrypted
        : encrypted instanceof ArrayBuffer
            ? new Uint8Array(encrypted)
            : new Uint8Array(encrypted.buffer, encrypted.byteOffset, encrypted.byteLength);
    if (bytes.length < HEADER_LENGTH + 16) {
        throw new Error('Cipher: Invalid encrypted data length (corrupted or truncated).');
    }
    for (let i = 0; i < MFS_MAGIC.length; i++) {
        if (bytes[i] !== MFS_MAGIC[i])
            throw new Error('Cipher: Invalid encrypted file signature (not a valid encrypted file format).');
    }
    const salt = bytes.subarray(4, 20);
    const iv = bytes.subarray(20, 32);
    const ciphertext = bytes.subarray(32);
    const key = await derivePasswordKey(password, salt, iterations, ['decrypt']);
    try {
        const decryptedBuf = await subtle.decrypt({ name: keys.TypeKey, iv: iv }, key, ciphertext);
        return new Uint8Array(decryptedBuf);
    }
    catch (err) {
        throw new Error('Cipher: Decryption failed. Incorrect password or data corrupted.', { cause: err });
    }
}
